<template>
  <l-feature-group>
    <l-marker
      key="startRef"
      :lat-lng="startRef"
      :icon="centerPinIcon"
    />
    <l-circle
      :lat-lng="startRef"
      :radius="circleRadius"
      :fill-color="color"
      :color="color"
      :weight="2"
      :fill-opacity="0.25"
      :interactive="false"
    />
    <l-circle-marker
      :lat-lng="endRef"
      :radius="5"
      :stroke="false"
      :fill-color="color"
      :fill-opacity="1"
      :interactive="false"
    >
      <l-tooltip
        :options="{
          permanent: true,
          direction: tooltipPosition,
          offset: tooltipOffset,
        }"
      >
        <div class="d-flex flex-column">
          <div>{{ Math.round(circleRadius / 1000) + 'km' }}</div>
          <div>{{ numOfAffectedStationsTooltip }}</div>
        </div>
      </l-tooltip>
    </l-circle-marker>
  </l-feature-group>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, watch } from 'vue';
import L, { Icon, latLng as toLatLng, LatLng } from 'leaflet';
import {
  LCircle,
  LCircleMarker,
  LFeatureGroup,
  LMarker,
  LTooltip,
} from '@vue-leaflet/vue-leaflet';
import type { Map as LeafletMap } from 'leaflet';
import { ToolPurpose } from '@/stores/map/map-model';
import { Station } from '@/stores/station/station-model';
import { useStationSelector } from '@/composables/useStationSelector';
import circleCenterMarker from '@/assets/map-marker.svg';
import markerShadow from '@/assets/pin/marker-shadow.png';
import StationStore from '@/stores/station/station-module';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'StationCircleSelector',
  components: { LMarker, LCircle, LFeatureGroup, LCircleMarker, LTooltip },
  props: {
    startAnchor: { type: Object as PropType<LatLng>, required: true },
    endAnchor: { type: Object as PropType<LatLng>, required: true },
    map: { type: Object as PropType<LeafletMap>, required: true },
    finished: { type: Boolean, required: true },
    purpose: { type: Object as PropType<ToolPurpose>, required: true },
  },
  emits: ['remove'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const stationStore = StationStore;
    const stationsInSelection = computed<Station[]>(() => {
      const stationsInRange: Station[] = [];
      for (const station of stationStore.allStations) {
        const distance = props.map.distance(
          props.startAnchor,
          toLatLng(station.lat, station.lon)
        );
        if (distance <= circleRadius.value) {
          stationsInRange.push(station);
        }
      }
      return stationsInRange;
    });
    const {
      startAnchor: startRef,
      endAnchor: endRef,
      color,
      numOfAffectedStationsTooltip,
      tooltipPosition,
      tooltipOffset,
      handleFinished,
    } = useStationSelector(props, stationsInSelection, t);

    const circleRadius = computed(() =>
      props.map.distance(startRef.value, endRef.value)
    );

    const centerPinIcon = computed<Icon>(() => {
      return L.icon({
        iconUrl: circleCenterMarker,
        shadowUrl: markerShadow,
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        popupAnchor: [1, -34],
        shadowSize: [36, 36],
        shadowAnchor: [12, 36],
      });
    });

    watch(() => props.finished, () => {
      if (props.finished) {
        emit('remove');
        handleFinished();
      }
    });

    return {
      startRef,
      endRef,
      color,
      numOfAffectedStationsTooltip,
      tooltipPosition,
      tooltipOffset,
      circleRadius,
      centerPinIcon,
    };
  },
});
</script>
<style scoped></style>
